<template>
  <no-ssr>
    <div>
      <div class="product-configurator">
        <div class="product-configurator-head">
          Ihr Zaunkonfigurator
          <span>Wählen Sie die Optionen für Ihren Wunschzaun:</span>
        </div>

        <ul v-if="bullets">
          <li v-for="bullet in bullets" :key="bullet" v-html="bullet" />
        </ul>

        <div class="product-configurator-content">
          <div v-if="getCurrentProduct.drahtstaerke && getCurrentProduct.drahtstaerke_link_sku">
            <div
              :class="{
                'product-configurator-option': true,
                'option-wire-strength': true,
                'strong': fenceWireStrength === '868'
              }"
            >
              <div class="row">
                <div class="configurator-option-head col-xs-4">
                  Drahtstärke
                </div>
                <div class="configurator-option-content col-xs-8">
                  <span
                    v-for="(strength, index) in ['656', '868']" :key="index"
                    :class="{ active: getCurrentProduct.drahtstaerke === strength, 'btn-check': true }"
                    @click="switchToLinkedProduct(getCurrentProduct.drahtstaerke_link_sku)"
                    @mouseover="()=>{ fenceWireStrength = strength }"
                    @mouseleave="fenceWireStrength = getCurrentProduct.drahtstaerke"
                  >
                    {{ strength }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="variants" v-if="getCurrentProduct.type_id =='configurable'">
            <!-- div
              class="error"
              v-if="getCurrentProduct.errors && !Object.values(getCurrentProduct.errors).every(x => (x === null || x === ''))"
            >
              {{ getCurrentProduct.errors | formatProductMessages }}
            </div -->

            <configurable-options :product="getCurrentProduct" v-if="getCurrentProduct.drahtstaerke && getCurrentProduct.drahtstaerke_link_sku" />

            <div class="product-configurator-option-wrapper" v-else>
              <div
                class="product-configurator-option"
                v-for="(option, index) in getCurrentProduct.configurable_options"
                :key="index"
              >
                <div class="row">
                  <div
                    class="configurator-option-head col-xs-4"
                    data-testid="variantsLabel"
                  >
                    {{ option.label }}
                  </div>

                  <div class="configurator-option-content col-xs-8">
                    <div class="configurator-option-value" v-if="selectedHeight">
                      <span>{{ selectedHeight.label }}</span>
                    </div>
                    <div class="configurator-option-change">
                      <a
                        @click="$parent.openHeightSelector"
                        class="configurator-option-change"
                      >Höhe ändern</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <product-custom-options
            v-if="getCurrentProduct.custom_options && getCurrentProduct.custom_options.length > 0"
            :product="getCurrentProduct"
          />

          <div class="product-configurator-summary">
            <div class="configurator-summary-inner">
              <div class="configurator-summary-price">
                <div v-if="configuredPrice && configuredOldPrice">
                  <div class="price-old">
                    <span>{{ configuredOldPrice | price }}</span> *
                  </div>
                  <div class="price-offer discount">
                    {{ configuredPrice | price }}
                    <span
                      class="badge badge-sm badge-secondary"
                    >Sonderpreis</span>
                  </div>
                </div>
                <div class="price-offer" v-else-if="configuredPrice">
                  {{ configuredPrice | price }}
                </div>
                <product-base-price :product="{... getCurrentProduct, baseprice_product_amount: getCalculatedLength() + getAdditionalFenceLength() }" :price="configuredPrice" />

                <div class="configurator-summary-tax">
                  <span v-if="storeCode === 'zaun24_de' && getCurrentProduct.tax_class_id === 1">inkl. 19% MwSt.,</span>
                  <span v-if="storeCode === 'zaun24_de' && getCurrentProduct.tax_class_id === 13">umsatzsteuerbefreit, gemäß §12 Abs. 3 UstG. *</span>
                  <span v-if="storeCode === 'zaun24_at' && getCurrentProduct.tax_class_id === 13">umsatzsteuerbefreit, gemäß § 28 Abs. 62 UstG. *</span>
                  <span v-else-if="storeCode === 'zaun24_at'">inkl. 20% MwSt.,</span>
                  <br>
                  <span class="product-delivery-time" v-if="getCurrentProduct.delivery_time"><router-link :to="localizedRoute('/lieferzeit')">{{ $t('Shipping Time') }} {{ getCurrentProduct.delivery_time }}</router-link>&nbsp;&nbsp;&middot;&nbsp;</span><matrixrates :product="getCurrentProduct" />
                </div>
              </div>

              <div
                class="configurator-summary-quantity"
                v-if="getCurrentProduct.type_id !== 'grouped' && getCurrentProduct.type_id !== 'bundle'"
              >
                <product-quantity
                  class="row m0 mb35"
                  v-if="getCurrentProduct.type_id !== 'grouped' && getCurrentProduct.type_id !== 'bundle'"
                  v-model="getCurrentProduct.qty"
                  :max-quantity="$parent.maxQuantity"
                  :loading="$parent.isStockInfoLoading"
                  :is-simple-or-configurable="$parent.isSimpleOrConfigurable"
                  :show-quantity="$parent.manageQuantity"
                  @error="$parent.handleQuantityError"
                />
              </div>
            </div>

            <div class="configurator-summary-action">
              <add-to-cart :product="getCurrentProduct" :disabled="$parent.isAddToCartDisabled" />
            </div>
          </div>
        </div>
      </div>
      <!-- zaun24-financing ref="financing" :price="configuredPrice" v-if="storeCode === 'zaun24_de'" / -->
    </div>
  </no-ssr>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddToCart from 'theme/components/core/AddToCart.vue'
import ProductCustomOptions from 'theme/components/core/ProductCustomOptions.vue'
import ProductQuantity from 'theme/components/core/ProductQuantity.vue'
import { getCustomOptionValues, getCustomOptionPriceDelta } from '@vue-storefront/core/modules/catalog/helpers/customOption'
import NoSSR from 'vue-no-ssr'
import ConfigurableOptions from 'theme/components/core/blocks/Product/ConfigurableOptions'

import ProductBasePrice from 'theme/components/core/blocks/Product/BasePrice'
import Matrixrates from 'src/modules/vsf-matrixrates/components/Matrixrates'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { parsePriceMatrix, getSortedLengths } from 'theme/helpers/fence'

import { prepareRelatedQuery } from '@vue-storefront/core/modules/catalog/queries/related'
import { formatProductLink } from '@vue-storefront/core/modules/url/helpers'

export default {
  props: {
    selectedHeight: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    AddToCart,
    ProductCustomOptions,
    Matrixrates,
    ProductQuantity,
    ProductBasePrice,
    ConfigurableOptions,
    // Zaun24Financing: () => import('theme/components/core/blocks/Custom/Zaun24Financing.vue'),
    'no-ssr': NoSSR
  },
  data () {
    return {
      fenceWireStrength: null
    }
  },
  watch: {
    getCurrentProduct (currentProduct, formerProduct) {
      this.suppliesSetCurrentProduct(currentProduct)
    }
  },
  computed: {
    ...mapGetters({
      getCurrentCustomOptions: 'product/getCurrentCustomOptions'
    }),
    getCurrentProduct () {
      return this.$parent.getCurrentProduct
    },
    storeCode () {
      return currentStoreView().storeCode
    },
    configuredPrice () {
      let price = 0
      price += this.getMatrixPrice()
      price += this.getAdditionalElementsPrice() * this.getAdditionalLength() / this.getPriceStep()

      return price
    },
    configuredOldPrice () {
      const discountFactor = parseFloat(this.getCurrentProduct.discount_factor)
      let oldPrice = 0
      if (discountFactor > 0) {
        oldPrice = this.configuredPrice / discountFactor
        oldPrice = Math.ceil(oldPrice).toFixed(2)
      }
      return oldPrice
    },
    description () {
      let fenceIntro = this.getCurrentProduct.fence_dynamic_intro;
      if (!fenceIntro) {
        fenceIntro = '<strong>{{length}} Meter {{name}}, {{height}} Höhe, {{color}}</strong></div><div style="margin-bottom:10px;">Bestehend aus:</div>'
      }

      let dynamicDescription = this.getCurrentProduct.fence_dynamic_description
      dynamicDescription = this.filterDynamicDescription(dynamicDescription)
      dynamicDescription = this.listify(dynamicDescription)

      let desc = '';
      desc += fenceIntro
      desc += dynamicDescription
      desc += this.getCurrentProduct.description

      return this.evalTemplate(desc)
    },
    bullets () {
      let bullets = this.getCurrentProduct.fence_dynamic_bullets
      bullets = this.filterDynamicDescription(bullets)
      return this.evalTemplate(bullets).split('\n').filter(line => line.trim() !== '')
    },
    templateVars () {
      let vars = {}

      for (const [key, value] of Object.entries(this.getCurrentProduct)) {
        vars[key] = value
      }

      vars = {
        ...vars,
        ...{
          length: this.getCalculatedLength() + this.getAdditionalFenceLength(),
          length_raw: this.getLength().toString().replace('.', ','),
          element_height: 1230,
          pillars_length: 2345,
          name: this.getCurrentProduct.name,
          sku: this.getColor() ? this.getColor().sku : '',
          height: this.getCurrentProduct.options[0].value,
          color: this.getColor() ? this.getColor().title : ''
        }
      }
      vars.elements = Math.round(vars.length / this.getFenceWidth())
      vars.pillars = vars.elements + 1
      return vars;
    }
  },
  methods: {
    ...mapActions({
      suppliesSetCurrentProduct: 'supplies/setCurrentProduct'
    }),
    async switchToLinkedProduct (sku) {
      const relatedProductsQuery = prepareRelatedQuery('sku', sku)

      const { items } = await this.$store.dispatch('product/findProducts', {
        query: relatedProductsQuery,
        size: 1
      })
      const scrollY = window.scrollY;

      this.$router.push(
        formatProductLink(items[0], this.storeCode)
      );

      setTimeout(() => {
        window.scrollTo(0, scrollY)
        const opts = this.$parent.$refs.fenceHeightSelector.options
        this.$parent.selectedHeight = opts[opts.length - 1].value
      }, 50)
    },
    getFencePrices () {
      return parsePriceMatrix(this.getCurrentProduct.price_matrix_fence)
    },
    getSortedLengths () {
      return getSortedLengths(this.getFencePrices())
    },
    getElementsPrice (color) {
      const elementPrices = parsePriceMatrix(
        this.getCurrentProduct.price_matrix_elements
      )
      return Object.values(elementPrices).find(() => true)[color]
    },
    getSelectedConfig (element) {
      return this.getOptionConfig(element, element.selectedIndex)
    },
    getOptionConfig (element, index) {
      return element.options[index].config
    },
    listify (str) {
      return '<ul><li>' +
        str.split('\n').filter((v) => v.trim() !== '').join('</li><li>') +
        '</li></ul>'
    },
    filterDynamicDescription (desc) {
      return desc.replace(/^\[(.+?):(.+?)\](.+)$/gm, this.filterCallback)
    },
    filterCallback (match, key, value, textContent, offset, original) {
      if (this.templateVars[key] === value) {
        return textContent
      }
      return '';
    },
    evalTemplate (template) {
      // template = this.filterDesc(template, this.templateVars)
      for (const [key, value] of Object.entries(this.templateVars)) {
        template = template.replace(
          new RegExp('{{' + key + '}}', 'g'),
          value
        )
      }
      return template
    },
    getAdditionalElementsPrice () {
      const color = this.getColor() ? this.getColor().sku : null
      if (!color || !this.getElementsPrice(color)) {
        return 0
      }
      return parseFloat(this.getElementsPrice(color))
    },
    getMatrixPrice () {
      const lengthConfig = this.getLengthConfig()
      const colorSku = this.getColor() ? this.getColor().sku : null
      if (
        typeof colorSku === 'undefined' ||
        typeof lengthConfig === 'undefined'
      ) {
        return 0
      }
      return parseFloat(lengthConfig[colorSku])
    },
    getLengthConfig () {
      const length = parseFloat(this.getMatrixLength(null)).toFixed(2)
      const cfg = this.getFencePrices()[length]
      return cfg
    },
    getCalculatedLength () {
      let length = this.getLength()
      const fenceWidth = this.getCurrentProduct.fence_width
      if (fenceWidth != null) {
        length =
          length % parseInt(fenceWidth) === 0
            ? length
            : this.intervalize(length, parseInt(fenceWidth))
      }
      return this.getMatrixLength(length)
    },
    getMatrixLength (length) {
      if (length === null) {
        length = this.getLength()
      }
      if (length === 0) {
        if (this.getCurrentProduct.fence_default_length) {
          return parseFloat(this.getCurrentProduct.fence_default_length.replace(',', '.'))
        }
        return this.getSortedLengths()[0]
      }

      let cfg = null
      const keys = this.getSortedLengths()
      for (let i = 0; i < keys.length; i++) {
        const _length = keys[i]
        if (_length >= length) {
          cfg = _length
          break
        }
      }
      if (cfg === null) {
        return parseFloat(keys[keys.length - 1])
      }
      return parseFloat(cfg)
    },
    getColor () {
      const option = this.getSelectedOptionByType('select')
      if (option.values) {
        const optionValue = option.values.find(
          val => val.option_type_id === parseInt(option.option_value)
        )
        return optionValue
      }
    },
    getSelectedOptionByType (type) {
      const option = this.getCurrentProduct.custom_options.find(
        opt => opt.type === type
      )
      const current = this.$store.state.product.current_custom_options[option.option_id]
      return { ...option, ...current }
    },
    getLength () {
      let length = 0
      const selectedOption = this.getSelectedOptionByType('field')
      if (
        selectedOption.option_value &&
        typeof selectedOption.option_value === 'string'
      ) {
        length = parseFloat(String(selectedOption.option_value).replace(',', '.'))
      }
      if (length > 9999) {
        length = 9999
      }
      return length
    },
    getFenceWidth () {
      if (this.getCurrentProduct.fence_width != null) {
        return parseInt(this.getCurrentProduct.fence_width)
      }
      return this.getPriceStep()
    },
    getPriceStep () {
      const steps = this.getSortedLengths()
      return steps[1] - steps[0]
    },
    getAdditionalFenceLength () {
      const step = this.getFenceWidth()
      return this._getAdditionalLength(step)
    },
    getAdditionalLength () {
      const step = this.getPriceStep()
      return this._getAdditionalLength(step)
    },
    _getAdditionalLength (step) {
      const additionalLength = this.getLength() - this.getCalculatedLength()
      if (additionalLength > 0) {
        return this.intervalize(additionalLength, step)
      }
      return 0
    },
    intervalize (num, step) {
      return Math.ceil(num / step) * step
    },
    getMinimumLength () {
      return (
        this.getSortedLengths()[0] -
        Math.floor(this.getFenceWidth() - 0.1)
      )
    }
  }
}
</script>
